/**
 * Match Roster Registry
 * Single source of truth for the players appearing in each demo match.
 * The simulation, commentary, edge alerts and AI Oracle MUST consume
 * `getMatchRoster(matchId)` instead of any hardcoded MEX/ARG names.
 */
import { trackEvent } from "@/lib/audit";
import { PREVIEW_MODE } from "@/lib/previewMode";

export type RosterPosition = "GK" | "DEF" | "MID" | "FWD";

export interface RosterPlayer {
  id: number;
  name: string;
  number: number;
  position: RosterPosition;
  rating?: number;
}

export interface MatchRoster {
  matchId: string;
  homeTeam: string;
  awayTeam: string;
  homePlayers: RosterPlayer[];
  awayPlayers: RosterPlayer[];
}

const FORMATION: RosterPosition[] = [
  "GK", "DEF", "DEF", "DEF", "DEF",
  "MID", "MID", "MID",
  "FWD", "FWD", "FWD",
];

function build(team: string, names: string[], numbers: number[], offset: number): RosterPlayer[] {
  return FORMATION.map((position, i) => ({
    id: offset + i,
    name: names[i] ?? `${team} #${numbers[i] ?? i + 1}`,
    number: numbers[i] ?? i + 1,
    position,
    rating: 78 + ((i * 7) % 14),
  }));
}

const ROSTERS: Record<string, MatchRoster> = {
  "mex-arg": {
    matchId: "mex-arg",
    homeTeam: PREVIEW_MODE ? "ESP" : "MEX",
    awayTeam: "ARG",
    homePlayers: PREVIEW_MODE
      ? build("ESP",
          ["Simón","Carvajal","Le Normand","Laporte","Cucurella","Rodri","Pedri","Fabián","Yamal","Morata","N. Williams"],
          [23,2,3,14,24,16,20,8,19,7,17], 0)
      : build("MEX",
          ["Ochoa","Araujo","Montes","Vásquez","Gallardo","Edson Álvarez","Chávez","Antuna","Lozano","Jiménez","Vega"],
          [13,4,5,3,23,4,7,17,22,9,16], 0),
    awayPlayers: build("ARG",
      ["Martínez","Molina","Romero","Otamendi","Acuña","De Paul","E. Fernández","Mac Allister","Messi","J. Álvarez","Di María"],
      [23,26,13,19,8,7,24,20,10,9,11], 11),
  },
  "bra-ger": {
    matchId: "bra-ger", homeTeam: "BRA", awayTeam: "GER",
    homePlayers: build("BRA",
      ["Alisson","Danilo","Marquinhos","T. Silva","Wendell","Casemiro","Bruno G.","Paquetá","Rodrygo","Vinícius Jr.","Richarlison"],
      [1,2,4,3,6,5,17,8,21,7,9], 0),
    awayPlayers: build("GER",
      ["Neuer","Kimmich","Rüdiger","Schlotterbeck","Raum","Goretzka","Andrich","Wirtz","Sané","Havertz","Musiala"],
      [1,6,2,23,3,8,17,10,19,7,14], 11),
  },
  "esp-fra": {
    matchId: "esp-fra", homeTeam: "ESP", awayTeam: "FRA",
    homePlayers: build("ESP",
      ["Simón","Carvajal","Le Normand","Laporte","Cucurella","Rodri","Pedri","Fabián","Yamal","Morata","N. Williams"],
      [23,2,3,14,24,16,20,8,19,7,17], 0),
    awayPlayers: build("FRA",
      ["Maignan","Koundé","Saliba","Upamecano","T. Hernández","Tchouaméni","Camavinga","Griezmann","Dembélé","Mbappé","Thuram"],
      [16,5,17,18,22,8,25,7,11,10,15], 11),
  },
  "usa-col": {
    matchId: "usa-col", homeTeam: "USA", awayTeam: "COL",
    homePlayers: build("USA",
      ["Turner","Dest","Richards","Robinson","Ream","Adams","McKennie","Musah","Reyna","Pulisic","Balogun"],
      [1,2,3,5,13,4,8,6,7,10,9], 0),
    awayPlayers: build("COL",
      ["Vargas","D. Muñoz","Y. Mina","Davinson","Mojica","Lerma","Uribe","J. Rodríguez","L. Díaz","Córdoba","Cuadrado"],
      [12,4,3,2,17,16,15,10,7,9,11], 11),
  },
  "eng-por": {
    matchId: "eng-por", homeTeam: "ENG", awayTeam: "POR",
    homePlayers: build("ENG",
      ["Pickford","Walker","Stones","Maguire","Shaw","Rice","Bellingham","Foden","Saka","Kane","Rashford"],
      [1,2,5,6,3,4,10,8,7,9,11], 0),
    awayPlayers: build("POR",
      ["Diogo Costa","Cancelo","Pepe","Rúben Dias","N. Mendes","Vitinha","Bruno F.","B. Silva","Leão","R. Jiménez","Ronaldo"],
      [22,20,3,4,19,23,8,10,17,15,7], 11),
  },
};

export function getMatchRoster(matchId: string): MatchRoster {
  return ROSTERS[matchId] ?? ROSTERS["mex-arg"];
}

export function listMatchRosters(): MatchRoster[] {
  return Object.values(ROSTERS);
}

/** One-line summary safe to inject into AI prompts. */
export function rosterSummary(matchId: string): string {
  const r = getMatchRoster(matchId);
  const fmt = (p: RosterPlayer) => `${p.name}(#${p.number} ${p.position})`;
  return `${r.homeTeam}: ${r.homePlayers.map(fmt).join(", ")} | ${r.awayTeam}: ${r.awayPlayers.map(fmt).join(", ")}`;
}

let _loaded = false;
export function ensureRostersLoaded() {
  if (_loaded) return;
  _loaded = true;
  void trackEvent("match_rosters_loaded", { count: Object.keys(ROSTERS).length });
}